/**
 * AISettings
 *
 * Site-wide AI configuration. Currently houses the Global Prompt —
 * a text prefix that is prepended to every bot's system prompt.
 *
 * This is Layer 1 of Rod's 3-layer PromptAssembler (AM-4013):
 *   Layer 1: Global Prompt (this)
 *   Layer 2: Bot Prompt (per AI Config)
 *   Layer 3: Channel Override (per AI Config per channel)
 */
import * as React from "react";
import { SiteSettings } from "../types";
import useSubscriptions from "@/hooks/useSubscriptions";

interface Props {
  siteSettings: SiteSettings;
  setSiteSettings: React.Dispatch<React.SetStateAction<SiteSettings>>;
}

export default function AISettings({ siteSettings, setSiteSettings }: Props) {
  const charCount = siteSettings.globalPrompt?.length ?? 0;
  const { subscription } = useSubscriptions();

  return (
    <div className="max-w-2xl">
      <h2 className="text-base font-semibold text-slate-800 mb-1">AI Settings</h2>
      <p className="text-sm text-slate-500 mb-6">
        Site-wide settings that apply to all AI bots and workflows.
      </p>

      {/* Global Prompt */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Global AI Prompt</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              This text is prepended to every bot's system prompt, site-wide.
              Use it for company-wide instructions like tone, compliance rules, or brand guidelines.
            </p>
          </div>
          {charCount > 0 && (
            <span className="text-xs text-slate-400 whitespace-nowrap ml-4">
              {charCount.toLocaleString()} chars
            </span>
          )}
        </div>

        <textarea
          value={siteSettings.globalPrompt ?? ""}
          onChange={(e) =>
            setSiteSettings((prev) => ({ ...prev, globalPrompt: e.target.value }))
          }
          placeholder={`Example:\nYou are a support assistant for Acme Corp. Always be professional and helpful. Never share internal pricing or competitor comparisons. If unsure, offer to connect the visitor with a human agent.`}
          rows={6}
          className="w-full text-sm border border-slate-200 rounded-lg px-4 py-3 resize-y focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
        />

        {/* How it works */}
        <div className="mt-4 bg-slate-50 border border-slate-100 rounded-lg px-4 py-3">
          <div className="text-xs font-medium text-slate-700 mb-2">How prompt assembly works</div>
          <div className="flex flex-col gap-2 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-violet-100 text-violet-700 rounded text-[10px] font-bold flex-shrink-0">1</span>
              <span><strong className="text-violet-700">Global Prompt</strong> — this field (applies to all bots)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-blue-100 text-blue-700 rounded text-[10px] font-bold flex-shrink-0">2</span>
              <span><strong className="text-blue-700">Bot Prompt</strong> — the system prompt on each AI Config (Bots page)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-amber-100 text-amber-700 rounded text-[10px] font-bold flex-shrink-0">3</span>
              <span><strong className="text-amber-700">Channel Override</strong> — per-channel tweaks (e.g., shorter for SMS, no URLs for IVR)</span>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            The final prompt sent to the AI is all three layers combined. Channel overrides can replace or suppress the bot prompt.
          </p>
        </div>
      </div>

      {/* Bot Outbound Communications */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mt-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Bot &amp; IVR Outbound Communications</h3>
        <p className="text-xs text-slate-500 mb-4">
          Allow AI bots and IVR workflows to send transactional SMS and email to visitors
          using the <code className="bg-slate-100 px-1 rounded">send_sms</code> and{" "}
          <code className="bg-slate-100 px-1 rounded">send_email</code> workflow skills.
          Add these skills to any AI node or IVR flow in the Workflows editor.
        </p>

        <div className="flex flex-col gap-3">
          {/* SMS */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Send SMS Skill
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {subscription?.enableBotSmsSkill
                  ? `Up to ${subscription.maxBotSmsPerHour === 0 ? "unlimited" : subscription.maxBotSmsPerHour} SMS/hour · max 3 per conversation`
                  : "Not enabled on your plan"}
              </div>
            </div>
            {subscription?.enableBotSmsSkill ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                Upgrade to Pro
              </span>
            )}
          </div>

          {/* Email */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Email Skill
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {subscription?.enableBotEmailSkill
                  ? `Up to ${subscription.maxBotEmailsPerHour === 0 ? "unlimited" : subscription.maxBotEmailsPerHour} emails/hour · max 2 per conversation`
                  : "Not enabled on your plan"}
              </div>
            </div>
            {subscription?.enableBotEmailSkill ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                Upgrade to Pro
              </span>
            )}
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-3">
          To use these skills, open any Workflow in the editor, add an AI node, and select
          "Send SMS" or "Send Email" from the Skills panel. The bot will ask for the
          phone/email before sending.
        </p>
      </div>

      {/* ── Conversation Quality & Agent Intelligence ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mt-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Agent Intelligence</h3>
        <p className="text-xs text-slate-500 mb-4">
          AI-powered quality scoring and smart reply for your support team.
          Requires a Professional or Business plan.
        </p>
        <div className="space-y-3">

          {/* Conversation Quality Scoring */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                Conversation Quality Scoring
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {subscription?.enableConversationQuality
                  ? "AI scores every resolved conversation 0–100 with sentiment + coaching note"
                  : "Not enabled on your plan — upgrade to Professional"}
              </div>
            </div>
            {subscription?.enableConversationQuality ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                Upgrade to Pro
              </span>
            )}
          </div>

          {/* Agent Smart Reply */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Agent Smart Reply
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {subscription?.enableAgentSmartReply
                  ? "Agents can click Suggest Reply to get an AI draft grounded in your KB"
                  : "Not enabled on your plan — upgrade to Professional"}
              </div>
            </div>
            {subscription?.enableAgentSmartReply ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                Upgrade to Pro
              </span>
            )}
          </div>

        </div>
        <p className="text-xs text-slate-400 mt-3">
          View scores and coaching notes at <strong>Reports → Agent Performance</strong>.
          Contact your account manager to enable these features.
        </p>
      </div>
    </div>
  );
}
